import type {
  BrfCompany,
  BrfMembershipNotation,
  Tenant,
} from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';
import { chunkIds } from '../utils';
import type { TCustomList } from './types';
import { fetchAllInstances } from '@/requests/api/fetch-all-instances';

export interface BrfMemberTenant extends Tenant {
  readonly activeBrfMembershipBrfCompanies: readonly BrfCompany[];
}

const MEMBERSHIP_DATE_WINDOW_START_MONTHS = 2;
const MEMBERSHIP_DATE_WINDOW_END_MONTHS = 4;

function getMembershipWindowIsoDates(): {
  readonly windowStart: string;
  readonly windowEnd: string;
} {
  const now = DateTime.now().startOf('day');
  return {
    windowStart: now
      .minus({ months: MEMBERSHIP_DATE_WINDOW_START_MONTHS })
      .toISODate()!,
    windowEnd: now
      .plus({ months: MEMBERSHIP_DATE_WINDOW_END_MONTHS })
      .toISODate()!,
  };
}

function notationOverlapsWindow(
  notation: BrfMembershipNotation,
  windowStart: string,
  windowEnd: string
): boolean {
  const from = notation.fromDate;
  if (from == null || from === '') {
    return false;
  }
  if (from > windowEnd) {
    return false;
  }
  const to = notation.toDate;
  if (to != null && to !== '' && to < windowStart) {
    return false;
  }
  return true;
}

function uniqueBrfCompanyIds(notations: BrfMembershipNotation[]): Set<string> {
  const ids = new Set<string>();
  for (const n of notations) {
    const id = n.brfCompany?.id;
    if (id) {
      ids.add(id);
    }
  }
  return ids;
}

export async function handleCustomBrfMemberNestedCase(
  listResponse: TCustomList<Tenant>
): Promise<TCustomList<BrfMemberTenant>> {
  const tenantIds = listResponse.data.reduce<Set<string>>((acc, tenant) => {
    if (tenant.id) {
      acc.add(tenant.id);
    }
    return acc;
  }, new Set());

  if (tenantIds.size === 0) {
    return {
      ...listResponse,
      data: listResponse.data.map((tenant) => ({
        ...tenant,
        activeBrfMembershipBrfCompanies: [],
      })),
    };
  }

  /** Avoid list fetches on non-BRF tenant tables (see `show_in_brf_context` on medlemmar). */
  const shouldFetchMemberships = listResponse.data.some(
    (tenant) => tenant.showInBrfContext === true
  );
  if (!shouldFetchMemberships) {
    return {
      ...listResponse,
      data: listResponse.data.map((tenant) => ({
        ...tenant,
        activeBrfMembershipBrfCompanies: [],
      })),
    };
  }

  const { windowStart, windowEnd } = getMembershipWindowIsoDates();
  const chunkedTenantIds = chunkIds(tenantIds);

  const notationResults = await Promise.all(
    chunkedTenantIds.map((chunk) =>
      fetchAllInstances<BrfMembershipNotation>({
        modelName: 'brfmembershipnotation',
        filters: {
          tenant: {
            __in: chunk.join(','),
          },
        },
        order: ['-createdAt'],
      })
    )
  );

  const allNotations: BrfMembershipNotation[] = [];
  for (const result of notationResults) {
    allNotations.push(...result.list);
  }

  const activeByTenantId = new Map<string, BrfMembershipNotation[]>();
  for (const notation of allNotations) {
    if (!notationOverlapsWindow(notation, windowStart, windowEnd)) {
      continue;
    }
    const tenantId = notation.tenant?.id;
    if (!tenantId) {
      continue;
    }
    const list = activeByTenantId.get(tenantId) ?? [];
    list.push(notation);
    activeByTenantId.set(tenantId, list);
  }

  const brfCompanyIdSet = uniqueBrfCompanyIds(
    [...activeByTenantId.values()].flat()
  );

  const brfCompanyById = new Map<string, BrfCompany>();
  if (brfCompanyIdSet.size > 0) {
    const chunkedCompanyIds = chunkIds(brfCompanyIdSet);
    const companyResults = await Promise.all(
      chunkedCompanyIds.map((chunk) =>
        fetchAllInstances<BrfCompany>({
          modelName: 'brfcompany',
          filters: { id: { __in: chunk.join(',') } },
          nested: ['company'],
        })
      )
    );

    for (const result of companyResults) {
      for (const brfCompany of result.list) {
        if (brfCompany.id) {
          brfCompanyById.set(brfCompany.id, brfCompany);
        }
      }
    }
  }

  return {
    ...listResponse,
    data: listResponse.data.map((tenant) => {
      const tid = tenant.id;
      const notations = tid ? (activeByTenantId.get(tid) ?? []) : [];
      const seen = new Set<string>();
      const activeBrfMembershipBrfCompanies: BrfCompany[] = [];
      for (const n of notations) {
        const cid = n.brfCompany?.id;
        if (!cid || seen.has(cid)) {
          continue;
        }
        seen.add(cid);
        const full = brfCompanyById.get(cid) ?? (n.brfCompany as BrfCompany);
        activeBrfMembershipBrfCompanies.push(full);
      }
      return {
        ...tenant,
        activeBrfMembershipBrfCompanies,
      };
    }),
  };
}
